import { Track as ProtoTrack } from "@opstel/gen/knop/ui/v1/model/track_pb";
import { z } from "zod/v3";
import { ZodOutputFor, createSchema, transformCoordinates, transformLength } from "../zod-utils";
import { PlainMessage } from "@bufbuild/protobuf";
import { DateTime } from "luxon";
import { DisabledTrackType } from "./disabledTrack";
import { WithBlockedInfrastructures } from "./types/context";
import { pointSchema } from "./point";
import { DirectionalList } from "./types/directionalList";
import { Length } from "@shared/common/utils/length";
import { Point } from "@opstel/geo/Point";

export interface BlockedDuration {
  startTime: DateTime;
  endTime: DateTime;
  blockedType: DisabledTrackType;
}

interface WithBlockedDurations {
  blockedDurations: BlockedDuration[];
}
interface WithGeometry {
  fromCode: string;
  toCode: string;
  coordinates: DirectionalList<Point>;
}

export interface Track {
  type: "Track";
  code: string;
  name: string;
  length: Length;
}
export interface TrackWithGeometry extends Track, WithGeometry {}
export interface TrackWithBlockedDurations extends Track, WithBlockedDurations {}

const baseTrackSchema = {
  code: z.string().min(1, "Track code should not be empty"),
  name: z.string().min(1, "Track name should not be empty"),
  lengthInMm: z.number(),
};

export const trackSchema = createSchema<PlainMessage<ProtoTrack>>(baseTrackSchema, {
  message: "Track should be set",
})
  .transform(transformLength)
  .transform((track) => ({ type: "Track", ...track })) satisfies ZodOutputFor<Track>;

export const trackWithGeometrySchema = z
  .object({
    ...baseTrackSchema,
    fromCode: z.string().min(1, "Track should have a from connection"),
    toCode: z.string().min(1, "Track should have a to connection"),
    coordinatesFromSourceToTarget: z.array(pointSchema).min(2, "Track should have coordinates"),
  })
  .transform(transformLength)
  .transform(transformCoordinates)
  .transform((track) => ({ ...track, type: "Track" })) satisfies ZodOutputFor<TrackWithGeometry>;

type TrackContext = WithBlockedInfrastructures;
export const Track = {
  isRecognizableTrack: (track: Track) => TrackCode.isRecognizableTrack(track.name),
  postProcessWithBlockedDurations: (
    track: Track,
    ctx: TrackContext,
  ): Track & WithBlockedDurations => ({
    ...track,
    blockedDurations: ctx.blockedInfrastructures
      .filter((blocked) => blocked.tracks.some(({ trackCode }) => trackCode === track.code))
      .map((blocked) => ({
        startTime: blocked.startTime,
        endTime: blocked.endTime,
        blockedType: blocked.tracks.find(({ trackCode }) => trackCode === track.code)!.type,
      })),
  }),
};
export const TrackCode = {
  // NOTE: Ideally based on data from KnoP/Kapi, but this heuristic seems to work for now.
  isRecognizableTrack: (trackCode: string) =>
    !TrackCode.isForeignTrack(trackCode) && !/^\d/.exec(trackCode),
  isForeignTrack: (trackCode: string) => trackCode.startsWith("TI_") || trackCode.startsWith("FT_"),
};
